package org.catharsis.spendtel.feature.presentation.web

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.outlined.AutoAwesome
import androidx.compose.material.icons.outlined.UploadFile
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavHostController
import org.catharsis.TVTally.theme.deepGreen
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.entitlementHint
import org.catharsis.spendtel.feature.data.creditsSummaryLine
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.presentation.expensereport.ExpenseReportStatusChipCompact
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.feature.presentation.home.DeleteExpenseReportDialog
import org.catharsis.spendtel.feature.presentation.home.HomeErrorText
import org.catharsis.spendtel.feature.presentation.home.HomeNewStoryButton
import org.catharsis.spendtel.feature.presentation.home.HomeReportLatestLabel
import org.catharsis.spendtel.feature.presentation.home.latestExpenseReportId
import org.catharsis.spendtel.feature.presentation.home.HomeProcessingStrip
import org.catharsis.spendtel.feature.presentation.home.HomeQuickSteps
import org.catharsis.spendtel.feature.presentation.web.WebReportPaywallModal
import org.catharsis.spendtel.feature.presentation.homeProgressLabel
import org.catharsis.spendtel.feature.presentation.isHomeBusy
import org.catharsis.spendtel.feature.presentation.rememberHomeScreenBindings
import org.catharsis.spendtel.feature.presentation.showsHomeProgress

@Composable
fun WebHomeScreen(navHostController: NavHostController) {
    val bindings = rememberHomeScreenBindings(navHostController)
    val scrollState = rememberScrollState()
    var reportPendingDelete by remember { mutableStateOf<ExpenseReport?>(null) }

    LaunchedEffect(bindings.state.isDeletingReportId, bindings.state.expenseReports) {
        val pending = reportPendingDelete ?: return@LaunchedEffect
        if (bindings.state.isDeletingReportId == null &&
            bindings.state.expenseReports.none { it.id == pending.id }
        ) {
            reportPendingDelete = null
        }
    }

    reportPendingDelete?.let { report ->
        DeleteExpenseReportDialog(
            report = report,
            isDeleting = bindings.state.isDeletingReportId == report.id,
            onDismiss = { reportPendingDelete = null },
            onConfirmDelete = { bindings.onDeleteReport(report.id) },
        )
    }

    val isBusy = bindings.state.isHomeBusy()
    val showsProgress = bindings.state.showsHomeProgress()
    val progressLabel = bindings.state.homeProgressLabel()

    Box(
        modifier = Modifier
            .fillMaxSize()
            .imePadding(),
    ) {
        WebAmbientPageBackground(modifier = Modifier.fillMaxSize())

        Column(modifier = Modifier.fillMaxSize()) {
            WebAppChrome(
                userName = bindings.state.currentUser?.username,
                email = bindings.state.currentUser?.emailAddress,
                creditsSummary = bindings.state.entitlement?.creditsSummaryLine(),
                accountMenuExpanded = bindings.accountMenuExpanded,
                onLogoClick = { /* already on dashboard */ },
                onAccountMenuToggle = bindings.onAccountMenuToggle,
                onAccountMenuDismiss = bindings.onAccountMenuDismiss,
                onOpenSettings = bindings.onOpenSettings,
                onSignOut = bindings.onSignOut,
            )

            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
                contentAlignment = Alignment.TopCenter,
            ) {
                when {
                    bindings.state.isLoading -> {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center,
                        ) {
                            CircularProgressIndicator(color = SpendTelReportColors.violet)
                        }
                    }

                    bindings.state.showEmptyState -> {
                        WebViewportScrollColumn(
                            scrollState = scrollState,
                            modifier = Modifier.fillMaxSize(),
                        ) {
                            Column(
                                modifier = Modifier.padding(horizontal = 24.dp, vertical = 40.dp),
                                verticalArrangement = Arrangement.spacedBy(32.dp),
                            ) {
                                WebHomeEmptyWelcome(
                                    userName = bindings.state.currentUser?.username,
                                    entitlementHint = bindings.state.entitlement?.entitlementHint(),
                                    isBusy = isBusy,
                                    showsProgress = showsProgress,
                                    uploadProgressLabel = progressLabel,
                                    uploadError = bindings.state.uploadError,
                                    onSelectStatements = bindings.onSelectStatements,
                                )
                            }
                        }
                    }

                    else -> {
                        WebViewportScrollColumn(
                            scrollState = scrollState,
                            modifier = Modifier.fillMaxSize(),
                        ) {
                            Column(
                                modifier = Modifier.padding(horizontal = 24.dp, vertical = 32.dp),
                                verticalArrangement = Arrangement.spacedBy(28.dp),
                            ) {
                            if (showsProgress) {
                                HomeProcessingStrip(
                                    label = progressLabel ?: "Crafting your story…",
                                )
                            }

                            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                                if (maxWidth < webCompactBreakpoint) {
                                    Column(
                                        modifier = Modifier.fillMaxWidth(),
                                        verticalArrangement = Arrangement.spacedBy(16.dp),
                                    ) {
                                        WebSectionHeader(
                                            title = "Your spending stories",
                                            subtitle = "${bindings.state.expenseReports.size} reports ready to explore",
                                        )
                                        Button(
                                            onClick = bindings.onSelectStatements,
                                            enabled = !isBusy,
                                            modifier = Modifier.fillMaxWidth(),
                                            shape = RoundedCornerShape(10.dp),
                                            colors = ButtonDefaults.buttonColors(
                                                containerColor = deepGreen,
                                                contentColor = Color.White,
                                            ),
                                        ) {
                                            Icon(
                                                imageVector = Icons.Outlined.UploadFile,
                                                contentDescription = null,
                                                modifier = Modifier
                                                    .padding(end = 8.dp)
                                                    .size(18.dp),
                                            )
                                            Text(
                                                text = "Upload statement",
                                                fontWeight = FontWeight.SemiBold,
                                            )
                                        }
                                    }
                                } else {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically,
                                    ) {
                                        WebSectionHeader(
                                            title = "Your spending stories",
                                            subtitle = "${bindings.state.expenseReports.size} reports ready to explore",
                                        )
                                        Button(
                                            onClick = bindings.onSelectStatements,
                                            enabled = !isBusy,
                                            shape = RoundedCornerShape(10.dp),
                                            colors = ButtonDefaults.buttonColors(
                                                containerColor = deepGreen,
                                                contentColor = Color.White,
                                            ),
                                        ) {
                                            Icon(
                                                imageVector = Icons.Outlined.UploadFile,
                                                contentDescription = null,
                                                modifier = Modifier
                                                    .padding(end = 8.dp)
                                                    .size(18.dp),
                                            )
                                            Text(
                                                text = "Upload statement",
                                                fontWeight = FontWeight.SemiBold,
                                            )
                                        }
                                    }
                                }
                            }

                            WebReportsGrid(
                                reports = bindings.state.expenseReports,
                                isDeletingReportId = bindings.state.isDeletingReportId,
                                onOpenReport = bindings.onOpenReport,
                                onDeleteReport = { reportPendingDelete = it },
                            )

                            bindings.state.uploadError?.let { error ->
                                HomeErrorText(message = error)
                            }

                            HomeNewStoryButton(
                                isBusy = isBusy,
                                onClick = bindings.onSelectStatements,
                            )
                            }
                        }
                    }
                }
            }
        }

        SnackbarHost(
            hostState = bindings.snackbarHostState,
            modifier = Modifier.align(Alignment.BottomCenter),
        )

        bindings.state.paywall?.let { paywall ->
            WebReportPaywallModal(
                message = paywall.message,
                priceLabel = paywall.priceLabel,
                isPurchasing = paywall.isPurchasing || bindings.state.isPurchasing,
                errorMessage = paywall.errorMessage,
                onDismiss = bindings.onDismissPaywall,
                onPurchase = bindings.onPurchaseReport,
            )
        }
    }
}

@Composable
private fun WebHomeEmptyWelcome(
    userName: String?,
    entitlementHint: String?,
    isBusy: Boolean,
    showsProgress: Boolean,
    uploadProgressLabel: String?,
    uploadError: String?,
    onSelectStatements: () -> Unit,
) {
    val greeting = userName?.let { "Welcome, $it" } ?: "Welcome to SpendTel"

    if (showsProgress) {
        HomeProcessingStrip(
            label = uploadProgressLabel ?: "Processing…",
        )
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(SpendTelGradients.hero)
            .padding(40.dp),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
            Text(
                text = greeting,
                style = MaterialTheme.typography.headlineLarge,
                fontWeight = FontWeight.Bold,
                color = Color.White,
            )
            Text(
                text = "Upload your first bank or card statement and we'll turn it into a personalized spending story in about two minutes.",
                style = MaterialTheme.typography.bodyLarge,
                color = Color.White.copy(alpha = 0.9f),
                lineHeight = 28.sp,
            )
            entitlementHint?.let { hint ->
                Text(
                    text = hint,
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color.White.copy(alpha = 0.85f),
                )
            }
            Button(
                onClick = onSelectStatements,
                enabled = !isBusy,
                shape = RoundedCornerShape(10.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color.White,
                    contentColor = SpendTelReportColors.ink,
                ),
            ) {
                Text(
                    text = when {
                        showsProgress -> uploadProgressLabel ?: "Processing…"
                        else -> "Upload your first statement"
                    },
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                )
            }
        }
    }

    WebAccentCard {
        Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
            Text(
                text = "How it works",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
            )
            HomeQuickSteps()
        }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(Color.White)
            .padding(20.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            imageVector = Icons.Outlined.AutoAwesome,
            contentDescription = null,
            tint = SpendTelReportColors.violet,
        )
        Text(
            text = "Your statements are encrypted and only used to build your report.",
            style = MaterialTheme.typography.bodyMedium,
            color = SpendTelReportColors.inkMuted,
        )
    }

    uploadError?.let { HomeErrorText(message = it) }
}

@Composable
private fun WebReportsGrid(
    reports: List<ExpenseReport>,
    isDeletingReportId: String?,
    onOpenReport: (String) -> Unit,
    onDeleteReport: (ExpenseReport) -> Unit,
) {
    val latestReportId = latestExpenseReportId(reports)

    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val useTwoColumns = maxWidth >= 800.dp && reports.size > 1

        if (useTwoColumns) {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                reports.chunked(2).forEach { rowReports ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        rowReports.forEach { report ->
                            WebReportCard(
                                report = report,
                                cardIndex = reports.indexOf(report),
                                isLatest = report.id == latestReportId,
                                isDeleting = isDeletingReportId == report.id,
                                onClick = { onOpenReport(report.id) },
                                onDelete = { onDeleteReport(report) },
                                modifier = Modifier.weight(1f),
                            )
                        }
                        if (rowReports.size == 1) {
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                reports.forEachIndexed { index, report ->
                    WebReportCard(
                        report = report,
                        cardIndex = index,
                        isLatest = report.id == latestReportId,
                        isDeleting = isDeletingReportId == report.id,
                        onClick = { onOpenReport(report.id) },
                        onDelete = { onDeleteReport(report) },
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun WebReportCard(
    report: ExpenseReport,
    cardIndex: Int,
    isLatest: Boolean = false,
    isDeleting: Boolean,
    onClick: () -> Unit,
    onDelete: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val gradient = SpendTelGradients.wrappedForIndex(cardIndex)
    val periodLabel = report.statementPeriodLabel

    val title = when {
        report.isProcessing -> "Unwrapping your story…"
        report.monthlyStory?.headline != null -> report.monthlyStory.headline
        report.financialPersonality?.label != null -> report.financialPersonality.label
        else -> "Spending story"
    }

    val subtitle = when {
        report.isProcessing -> periodLabel
        report.summary != null -> formatCurrency(report.summary.totalSpending, report.summary.currency)
        else -> periodLabel
    }

    WebAccentCard(
        modifier = modifier.clickable(enabled = !isDeleting, onClick = onClick),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(width = 6.dp, height = 72.dp)
                    .clip(RoundedCornerShape(3.dp))
                    .background(gradient),
            )

            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Text(
                        text = periodLabel,
                        style = MaterialTheme.typography.labelSmall,
                        color = SpendTelReportColors.inkMuted,
                    )
                    ExpenseReportStatusChipCompact(status = report.status)
                    if (isLatest) {
                        HomeReportLatestLabel()
                    }
                }
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = SpendTelReportColors.ink,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodyMedium,
                    color = SpendTelReportColors.inkMuted,
                )
            }

            if (isDeleting) {
                CircularProgressIndicator(
                    modifier = Modifier.size(22.dp),
                    strokeWidth = 2.dp,
                    color = SpendTelReportColors.violet,
                )
            } else {
                IconButton(onClick = onDelete) {
                    Icon(
                        imageVector = Icons.Filled.Delete,
                        contentDescription = "Delete report",
                        tint = SpendTelReportColors.inkMuted.copy(alpha = 0.55f),
                    )
                }
            }
        }
    }
}
